import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, X, Check, HardDrive, Users, Calendar, Zap, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { STORE_PLUS_MONTHLY_STORE_ID } from '../../utils/storeIapConstants';

const MsStoreIAPModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('shared');
  const { authToken, refreshUser } = useAuth();

  const plan = {
    key: 'monthly',
    storeId: STORE_PLUS_MONTHLY_STORE_ID,
    label: t('msStoreIAPModal.plans.monthly.label'),
    desc: t('msStoreIAPModal.plans.monthly.desc'),
  };

  const FEATURES = [
    { icon: HardDrive, text: t('msStoreIAPModal.features.upload') },
    { icon: Users, text: t('msStoreIAPModal.features.collabEdit') },
    { icon: Users, text: t('msStoreIAPModal.features.groups') },
    { icon: Calendar, text: t('msStoreIAPModal.features.calendar') },
    { icon: Zap, text: t('msStoreIAPModal.features.support') },
  ];
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'error'|'info', message }

  const handlePurchase = async () => {
    if (loading || !window.api?.storePurchase) return;

    setLoading(true);
    setResult(null);

    try {
      const purchaseResult = await window.api.storePurchase(plan.storeId);
      const status = purchaseResult?.status;

      if (status === 'Succeeded' || status === 'AlreadyPurchased') {
        // Purchase UX only — Plus requires Microsoft server-side verify
        if (!authToken) {
          setResult({ type: 'error', message: t('msStoreIAPModal.verifyFailed') });
        } else {
          try {
            const { syncMicrosoftStoreEntitlement } = await import('../../utils/syncMicrosoftStoreEntitlement');
            const verified = await syncMicrosoftStoreEntitlement({ token: authToken, force: true });
            if (verified.ok && verified.isPlus) {
              await refreshUser();
              setResult({
                type: 'success',
                message: status === 'AlreadyPurchased'
                  ? t('msStoreIAPModal.alreadyPurchased')
                  : t('msStoreIAPModal.purchaseSuccess', { plan: plan.label }),
              });
            } else if (verified.ok && !verified.isPlus) {
              setResult({ type: 'error', message: t('msStoreIAPModal.verifyNoEntitlement') });
            } else {
              setResult({
                type: 'error',
                message: verified.transient
                  ? t('msStoreIAPModal.verifyTransient')
                  : t('msStoreIAPModal.verifyFailed'),
              });
            }
          } catch (backendErr) {
            console.error('[MsStoreIAP] Verify error');
            setResult({ type: 'error', message: t('msStoreIAPModal.verifyFailed') });
          }
        }
      } else if (status === 'NotPurchased') {
        setResult({ type: 'info', message: t('msStoreIAPModal.purchaseCancelled') });
      } else if (status === 'NetworkError') {
        setResult({ type: 'error', message: t('msStoreIAPModal.networkError') });
      } else if (status === 'ServerError') {
        setResult({ type: 'error', message: t('msStoreIAPModal.serverError') });
      } else {
        setResult({ type: 'error', message: t('msStoreIAPModal.unexpectedError', { status: status || t('msStoreIAPModal.unknownStatus') }) });
      }
    } catch (err) {
      console.error('[MsStoreIAP] Purchase error');
      setResult({ type: 'error', message: t('msStoreIAPModal.purchaseStartFailed') });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setResult(null);
    onClose();
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[99000] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

          <motion.div
            className="relative w-full max-w-md bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.2 }}
          >
            <button
              onClick={handleClose}
              disabled={loading}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-[#4d6a8a] hover:text-[#c8d8eb] hover:bg-white/[0.06] transition-colors z-10 disabled:opacity-40"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="px-6 pt-6 pb-4 text-center border-b border-white/[0.06]">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-2xl bg-amber-500/15 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-amber-400" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-white mb-1">{t('msStoreIAPModal.title')}</h2>
              <p className="text-sm text-[#4d6a8a]">{t('msStoreIAPModal.subtitle')}</p>
            </div>

            <div className="px-6 py-4 border-b border-white/[0.06]">
              <div className="space-y-2.5">
                {FEATURES.map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#3d8ef0]/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-[#3d8ef0]" />
                    </div>
                    <span className="text-sm text-[#c8d8eb]">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {result && (
              <div className={`mx-6 mt-4 flex items-start gap-3 px-4 py-3 rounded-xl text-sm ${
                result.type === 'success'
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300'
                  : result.type === 'info'
                  ? 'bg-blue-500/10 border border-blue-500/20 text-blue-300'
                  : 'bg-red-500/10 border border-red-500/20 text-red-300'
              }`}>
                {result.type === 'success'
                  ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                }
                <span>{result.message}</span>
              </div>
            )}

            <div className="px-6 py-5 space-y-3">
              <button
                onClick={handlePurchase}
                disabled={loading || result?.type === 'success'}
                className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl bg-[#161f31] border border-white/[0.07] hover:bg-[#1a2640] hover:border-[#3d8ef0]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="text-left">
                  <p className="text-sm font-semibold text-white">
                    {plan.label} {t('msStoreIAPModal.subscriptionSuffix')}
                  </p>
                  <p className="text-xs text-[#4d6a8a]">{plan.desc}</p>
                </div>
                {loading ? (
                  <Loader2 className="w-5 h-5 text-[#3d8ef0] animate-spin flex-shrink-0" />
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#4d6a8a] group-hover:text-[#3d8ef0] transition-colors">
                      {t('msStoreIAPModal.viewInStore')}
                    </span>
                    <div className="w-7 h-7 rounded-full bg-[#3d8ef0]/15 group-hover:bg-[#3d8ef0]/25 flex items-center justify-center transition-colors flex-shrink-0">
                      <Crown className="w-3.5 h-3.5 text-[#3d8ef0]" />
                    </div>
                  </div>
                )}
              </button>

              {result?.type === 'success' && (
                <button
                  onClick={handleClose}
                  className="w-full py-3 rounded-xl bg-[#3d8ef0] hover:bg-[#5ca3f5] text-white text-sm font-semibold transition-colors"
                >
                  {t('msStoreIAPModal.greatContinue')}
                </button>
              )}

              <p className="text-center text-[11px] text-[#3d5470] pt-1">
                {t('msStoreIAPModal.priceDisclaimer')}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default MsStoreIAPModal;
